import { classifyInboxMessage } from "../../../common/inboxMessageClassification.ts";
import { idb } from "../db/index.ts";
import g from "./g.ts";

// N4-4: derive the bottom-tab red-dot signals that the UI can't infer from other
// synced state. The market dot comes from playMenuOptions (already synced), so
// the worker-owned signals live here:
//   home = an unread must-decide inbox message exists (owner is waiting)
//   market = the current user team has at least one real negotiation queued
//   team = the user roster is illegal — too few players (short) or genuinely
//          over the roster limit. Note: an exactly-full legal roster (count === max) is
//          the dashboard's "rosterFull" info alert, NOT a pending decision, so
//          it deliberately does NOT light the dot (red-dot discipline: pure
//          informational state never lights a tab).
// Computed inside updatePlayMenu, which already runs on navigation + every state
// mutation, so this piggybacks the existing sync instead of adding its own query
// loop. Reads are in-memory cache. The inbox scan deliberately covers all
// messages so an older unread decision cannot disappear behind a preview limit.
export const deriveMobileTabAlerts = ({
	maxRosterSize,
	messages,
	minRosterSize,
	negotiations,
	rosterCount,
	userTid,
}: {
	maxRosterSize: number;
	messages: Parameters<typeof classifyInboxMessage>[0][];
	minRosterSize: number;
	negotiations: { tid: number }[];
	rosterCount: number;
	userTid: number;
}) => ({
	home: messages.some(
		(message) => classifyInboxMessage(message) === "task",
	),
	market: negotiations.some((negotiation) => negotiation.tid === userTid),
	team: rosterCount < minRosterSize || rosterCount > maxRosterSize,
});

const getMobileTabAlerts = async (): Promise<{
	home: boolean;
	market: boolean;
	team: boolean;
}> => {
	// Spectator / multi-team control: the user isn't the one making these
	// decisions, so no personal red dots (mirrors updateOwnerMood's guard).
	if (g.get("spectator") || g.get("userTids").length !== 1) {
		return { home: false, market: false, team: false };
	}

	try {
		const userTid = g.get("userTid");
		const [players, messages, negotiations] = await Promise.all([
			idb.cache.players.indexGetAll("playersByTid", userTid),
			idb.getCopies.messages(undefined, "noCopyCache"),
			idb.cache.negotiations.getAll(),
		]);
		const rosterCount = players.length;
		const minRosterSize = g.get("minRosterSize");
		const maxRosterSize = g.get("maxRosterSize");

		return deriveMobileTabAlerts({
			maxRosterSize,
			messages,
			minRosterSize,
			negotiations,
			rosterCount,
			userTid,
		});
	} catch {
		// Never let a red-dot computation break the play menu.
		return { home: false, market: false, team: false };
	}
};

export default getMobileTabAlerts;
